// src/hooks/useAuth.js

import { useState, useEffect } from 'react';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore'; // Added setDoc
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut
} from 'firebase/auth';

const useAuth = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchUserProfile = async (user) => {
        if (!user) {
            return null;
        }

        try {
            const docRef = doc(db, "users", user.uid);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                // User found - return complete object
                return {
                    uid: user.uid,
                    email: user.email,
                    emailVerified: user.emailVerified,
                    displayName: user.displayName,
                    ...docSnap.data(),
                    id: user.uid
                };
            } else {
                // CRITICAL FIX: User exists in Auth but not in Firestore
                console.warn("Documento de usuário não encontrado no Firestore para UID:", user.uid);

                // Option 1: Create the user document automatically
                try {
                    console.log("Creating user document for UID:", user.uid);
                    const defaultUserData = {
                        nome: user.displayName || user.email?.split('@')[0] || 'Usuário',
                        email: user.email,
                        role: 'Operacional', // Default role
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        status: 'active'
                    };

                    await setDoc(docRef, defaultUserData);
                    console.log("User document created successfully");

                    // Return complete user object with the new data
                    return {
                        uid: user.uid,
                        email: user.email,
                        emailVerified: user.emailVerified,
                        displayName: user.displayName,
                        ...defaultUserData,
                        id: user.uid
                    };
                } catch (createError) {
                    console.error("Error creating user document:", createError);

                    // Option 2: Return minimal user object with defaults
                    // This ensures the app doesn't break
                    return {
                        uid: user.uid,
                        email: user.email || 'unknown@email.com',
                        nome: user.displayName || user.email?.split('@')[0] || 'Usuário',
                        role: 'Operacional', // Important! Default role
                        emailVerified: user.emailVerified || false,
                        displayName: user.displayName || '',
                        id: user.uid
                    };
                }
            }
        } catch (error) {
            console.error("Erro ao buscar perfil do Firestore:", error);

            // Always return a valid user object with defaults on error
            return {
                uid: user.uid,
                email: user.email || 'unknown@email.com',
                nome: user.displayName || user.email?.split('@')[0] || 'Usuário',
                role: 'Operacional',
                emailVerified: user.emailVerified || false,
                displayName: user.displayName || '',
                id: user.uid
            };
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            try {
                if (user) {
                    const profile = await fetchUserProfile(user);
                    setCurrentUser(profile);
                } else {
                    setCurrentUser(null);
                }
            } catch (error) {
                console.error("Error in auth state change:", error);
                setCurrentUser(null);
            } finally {
                setLoading(false); // Always set loading to false
            }
        });

        return () => unsubscribe();
    }, []);

    const login = async (email, password) => {
        try {
            setLoading(true);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            // The onAuthStateChanged above will handle loading the profile
            return { success: true };
        } catch (error) {
            setLoading(false);
            console.error("Erro no login:", error.message);
            return { success: false, error: error.message };
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    return {
        currentUser,
        loading,
        login,
        logout,
    };
};

export default useAuth;